import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = axios.create({
  baseURL: `${BASE}/api`,
  // Send/receive httpOnly cookies (fl_token, fl_admin_token) on every request.
  // Backend keeps accepting Authorization/X-Admin-Token headers as fallback.
  withCredentials: true,
  timeout: 30000, // 30s — matches backend socketTimeoutMS
});

// Retry idempotent GETs on transient failures (5xx, network errors) up to 2 times
// with exponential backoff. Skips 4xx (client errors — retry doesn't help).
API.interceptors.response.use(undefined, async (error) => {
  const cfg = error.config;
  if (!cfg) return Promise.reject(error);
  const status = error.response?.status;
  const isRetryable = !error.response || (status >= 500 && status < 600);
  const isSafeMethod = !cfg.method || ['get', 'head'].includes(cfg.method.toLowerCase());
  cfg.__retryCount = cfg.__retryCount || 0;
  if (isRetryable && isSafeMethod && cfg.__retryCount < 2) {
    cfg.__retryCount += 1;
    const delay = 300 * Math.pow(2, cfg.__retryCount - 1);
    await new Promise(r => setTimeout(r, delay));
    return API(cfg);
  }
  return Promise.reject(error);
});

// User token
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('fl_token');
  const adminToken = localStorage.getItem('fl_adminToken');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (adminToken && !config.headers['X-Admin-Token']) {
    config.headers['X-Admin-Token'] = adminToken;
  }
  return config;
});

export const authAPI = {
  login: (email, password) => API.post('/auth/login', { email, password }).then(r => r.data),
  logout: () => API.post('/auth/logout').then(r => r.data).catch(() => {}),
  me: () => API.get('/auth/me').then(r => r.data),
  updateMe: (patch) => API.put('/auth/me', patch).then(r => r.data),
  signup: (data) => API.post('/auth/signup', data).then(r => r.data),
};

// Public (no auth) — used by signup Pix screen and landing page
export const publicAPI = {
  plan: () => API.get('/plan/public').then(r => r.data),
};

export const adminAPI = {
  login: (password) => API.post('/admin/login', { password }).then(r => r.data),
  logout: () => API.post('/admin/logout').then(r => r.data).catch(() => {}),
  verify: () => API.get('/admin/verify').then(r => r.data),
  changePassword: (current, next) => API.post('/admin/change-password', { current, next }).then(r => r.data),
  listUsers: () => API.get('/users').then(r => r.data),
  createUser: (data) => API.post('/users', data).then(r => r.data),
  updateUser: (id, patch) => API.put(`/users/${id}`, patch).then(r => r.data),
  renewUser: (id, days = 30) => API.post(`/users/${id}/renew`, { days }).then(r => r.data),
  activateUser: (id) => API.post(`/users/${id}/activate`).then(r => r.data),
  blockUser: (id) => API.post(`/users/${id}/block`).then(r => r.data),
  resendPix: (id) => API.post(`/users/${id}/resend-pix`).then(r => r.data),
  deleteUser: (id) => API.delete(`/users/${id}`).then(r => r.data),
  listAllAlbums: () => API.get('/albums/all').then(r => r.data),
  deleteAlbum: (code) => API.delete(`/admin/albums/${code}`).then(r => r.data),
  getPlan: () => API.get('/admin/plan').then(r => r.data),
  updatePlan: (data) => API.put('/admin/plan', data).then(r => r.data),
  listDownloads: (limit = 200) => API.get(`/admin/downloads?limit=${limit}`).then(r => r.data),
  listAuditLogs: (limit = 300, kind) => API.get(`/admin/audit-logs?limit=${limit}${kind ? `&kind=${kind}` : ''}`).then(r => r.data),
};

export const albumAPI = {
  listMine: () => API.get('/albums').then(r => r.data),
  create: (data) => API.post('/albums', data).then(r => r.data),
  get: (code) => API.get(`/albums/${code}`).then(r => r.data),
  update: (code, patch) => API.put(`/albums/${code}`, patch).then(r => r.data),
  remove: (code) => API.delete(`/albums/${code}`).then(r => r.data),
  recordSale: (code, amount, count, photoIds = [], mode = 'individual') =>
    API.post(`/albums/${code}/sale`, { amount, count, photoIds, mode }).then(r => r.data),
  listPurchases: (code) => API.get(`/albums/${code}/purchases`).then(r => r.data),
  releasePurchase: (code, purchaseId) => API.post(`/albums/${code}/purchases/${purchaseId}/release`).then(r => r.data),
  revokePurchase: (code, purchaseId) => API.post(`/albums/${code}/purchases/${purchaseId}/revoke`).then(r => r.data),
  cancelPurchase: (code, purchaseId) => API.post(`/albums/${code}/purchases/${purchaseId}/cancel`).then(r => r.data),
  addPhotos: (code, photos) => API.post(`/albums/${code}/photos`, { photos }).then(r => r.data),
  removePhoto: (code, photoId) => API.delete(`/albums/${code}/photos/${photoId}`).then(r => r.data),
  extend: (code, hours = 24) => API.post(`/albums/${code}/extend`, { hours }).then(r => r.data),
  pendingOrders: () => API.get('/notifications/pending-orders').then(r => r.data),
  cancelMyOrders: (code) => API.post(`/albums/${code}/cancel-my-orders`).then(r => r.data),
  logDownload: (code, count, photoIds = [], clientName = '') =>
    API.post(`/albums/${code}/log-download`, { count, photoIds, clientName, device: (navigator.userAgent || '').slice(0, 240) }).then(r => r.data).catch(() => {}),
  downloadNotifications: () => API.get('/notifications/downloads').then(r => r.data),
  listClients: () => API.get('/clients').then(r => r.data),
};
